import React, { useState, useEffect } from 'react';
import { Card, Button, Table, Tag, Statistic, Row, Col, message, Modal, Form, Input, Switch, Space, DatePicker, Select } from 'antd';
import { PlusOutlined, UnorderedListOutlined, AppstoreOutlined, ClockCircleOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { processAPI } from '../services/api';

const { RangePicker } = DatePicker;

const Dashboard = () => {
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [searchForm] = Form.useForm();
  const [searchParams, setSearchParams] = useState({
    name: '',
    status: '',
    start_date: '',
    end_date: ''
  });
  const navigate = useNavigate();

  // 获取流程列表
  useEffect(() => {
    fetchProcesses();
  }, [pagination.current, pagination.pageSize, searchParams]);

  const fetchProcesses = async () => {
    setLoading(true);
    try {
      const response = await processAPI.getProcesses({
        page: pagination.current,
        per_page: pagination.pageSize,
        ...searchParams
      });
      setProcesses(response.data.data);
      setPagination({
        ...pagination,
        total: response.data.pagination.total,
        current: response.data.pagination.current_page,
        pageSize: response.data.pagination.per_page
      });
    } catch (error) {
      console.error('获取流程列表错误:', error);
      message.error('获取流程列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (values) => {
    // 处理日期范围
    let start_date = '';
    let end_date = '';
    if (values.dateRange) {
      start_date = values.dateRange[0].format('YYYY-MM-DD');
      end_date = values.dateRange[1].format('YYYY-MM-DD');
    }
    
    setSearchParams({
      name: values.name || '',
      status: values.status || '',
      start_date,
      end_date
    });
    
    // 重置页码到第一页
    setPagination({
      ...pagination,
      current: 1
    });
  };

  // 处理重置
  const handleReset = () => {
    searchForm.resetFields();
    setSearchParams({
      name: '',
      status: '',
      start_date: '',
      end_date: ''
    });
    
    // 重置页码到第一页
    setPagination({
      ...pagination,
      current: 1
    });
  };

  // 处理创建流程
  const handleCreateProcess = () => {
    createForm.resetFields();
    setCreateModalVisible(true);
  };

  // 处理提交创建流程
  const handleCreateProcessSubmit = async (values) => {
    setLoading(true);
    try {
      // 获取当前登录用户的ID
      const currentUser = JSON.parse(localStorage.getItem('userInfo'));
      const createdBy = currentUser ? currentUser.id : 1; // 默认ID为1
      
      const response = await processAPI.createProcess({
        name: values.name,
        description: values.description,
        include_uat: values.includeUat,
        created_by: createdBy
      });
      message.success('流程创建成功');
      setCreateModalVisible(false);
      fetchProcesses(); // 刷新流程列表
    } catch (error) {
      console.error('创建流程错误:', error);
      message.error('创建流程失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理查看流程详情
  const handleViewProcess = (processId) => {
    navigate(`/process/${processId}`);
  };

  // 处理删除流程
  const handleDeleteProcess = (processId, processName) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除流程 "${processName}" 吗？此操作不可撤销。`,
      okText: '确定',
      cancelText: '取消',
      onOk: async () => {
        try {
          await processAPI.deleteProcess(processId);
          message.success('流程删除成功');
          fetchProcesses(); // 刷新流程列表
        } catch (error) {
          console.error('删除流程错误:', error);
          message.error('删除流程失败');
        }
      }
    });
  };

  // 表格列配置
  const columns = [
    {
      title: '流程名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <a onClick={() => handleViewProcess(record.id)}>{text}</a>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        let color = '';
        let text = '';
        switch (status) {
          case 'pending':
            color = 'default';
            text = '未开始';
            break;
          case 'in_progress':
            color = 'blue';
            text = '进行中';
            break;
          case 'completed':
            color = 'green';
            text = '已完成';
            break;
          default:
            color = 'default';
            text = '未知';
        }
        return <Tag color={color}>{text}</Tag>;
      }
    },
    {
      title: '创建人',
      dataIndex: 'creator_name',
      key: 'creator_name',
      render: (name) => name || '未知用户'
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (time) => new Date(time).toLocaleString()
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => handleViewProcess(record.id)}>
            查看详情
          </Button>
          {record.status !== 'completed' && (
            <Button type="link" danger onClick={() => handleDeleteProcess(record.id, record.name)}>
              删除
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="glass-page-title">仪表盘</h1>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card className="glass-card glass-statistic">
            <Statistic 
              title="总流程数" 
              value={processes.length} 
              prefix={<AppstoreOutlined />} 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="glass-card glass-statistic">
            <Statistic 
              title="进行中流程" 
              value={processes.filter(p => p.status === 'pending').length} 
              prefix={<ClockCircleOutlined />} 
              valueStyle={{ color: '#108ee9' }} 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="glass-card glass-statistic">
            <Statistic 
              title="已完成流程" 
              value={processes.filter(p => p.status === 'completed').length} 
              prefix={<UnorderedListOutlined />} 
              valueStyle={{ color: '#52c41a' }} 
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card className="glass-card">
            <Button 
              type="primary" 
              icon={<PlusOutlined />} 
              onClick={handleCreateProcess}
              style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              创建新流程
            </Button>
          </Card>
        </Col>
      </Row>

      {/* 搜索表单 */}
      <Card className="glass-card" style={{ marginBottom: 24 }}>
        <Form
          form={searchForm}
          className="glass-form"
          layout="inline"
          onFinish={handleSearch}
          initialValues={{ status: '' }}
        >
          <Form.Item name="name" label="流程名称">
            <Input placeholder="请输入流程名称" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select placeholder="请选择状态">
              <Select.Option value="">全部</Select.Option>
              <Select.Option value="in_progress">进行中</Select.Option>
              <Select.Option value="completed">已完成</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange" label="创建时间">
            <RangePicker style={{ width: 300 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
              搜索
            </Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={handleReset} icon={<ReloadOutlined />}>
              重置
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* 最近流程列表 */}
      <Card className="glass-card" title="流程列表" style={{ marginBottom: 24 }}>
        <div className="glass-table">
          <Table 
            columns={columns} 
            dataSource={processes} 
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: ['5', '10', '20'],
              onChange: (page, pageSize) => {
                setPagination({
                  ...pagination,
                  current: page,
                  pageSize: pageSize
                });
              },
              showTotal: (total) => `共 ${total} 条记录`
            }}
          />
        </div>
      </Card>

      {/* 待办任务列表 */}
      <Card className="glass-card" title="待办任务" style={{ marginBottom: 24 }}>
        <div style={{ textAlign: 'center', padding: 48, color: 'rgba(255, 255, 255, 0.5)' }}>
          待办任务功能开发中
        </div>
      </Card>

      {/* 创建流程模态框 */}
      <Modal
        className="glass-modal"
        title="创建新流程"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
      >
        <Form
          form={createForm}
          className="glass-form"
          onFinish={handleCreateProcessSubmit}
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="流程名称"
            rules={[{ required: true, message: '请输入流程名称' }]}
          >
            <Input placeholder="请输入流程名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="流程描述"
          >
            <Input.TextArea placeholder="请输入流程描述" rows={4} />
          </Form.Item>

          <Form.Item
            name="includeUat"
            label="包含UAT流程"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch checkedChildren="是" unCheckedChildren="否" />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <Button onClick={() => setCreateModalVisible(false)}>取消</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                创建
              </Button>
            </div>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Dashboard;
